import './productSpecs.scss';

import React, { useState } from 'react';

import ProductAttributeItem from './ProductAttributeItem';
import { Tooltip } from 'react-tooltip';
import classNames from 'classnames';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

const TABLE_REF: Record<string, number[]> = {
    EffOpti: [8, 9],
    Emiss: [1],
    PWMaSoi: [4],
    RenMoyGB: [5, 7],
    ReMoyGBmi: [5, 6],
    PwThMax: [4, 5],
    PwThMin: [4, 5],
    ReMoyGEmi: [5, 6],
    Surfi: [3],
    TempComb: [3],
    RenMoyGE: [5, 7],
    TauxCO: [10],
    RenOTC: [11],
};

type ProductSpecsProps = {
    product: Queries.ProductJsonTemplateFragment;
};

export default function ProductSpecs(props: ProductSpecsProps) {
    const { product } = props;

    const { t } = useTranslation('templatePages');

    const [partIndex, setPartIndex] = useState(0);
    const [showMore, setShowMore] = useState(false);

    const { currentLocale } = usePageLocalization();

    const createTip = (attributeName: string): JSX.Element => {
        return TABLE_REF[attributeName] ? (
            <>
                &nbsp;
                {TABLE_REF[attributeName].map((ref, index) => {
                    const tooltipRef = `footref-${attributeName}-${index}`;
                    return (
                        <React.Fragment key={`table_ref-${index}`}>
                            <span className='note-indicator-slide'>
                                (
                                <span className='tool-tip-ref' id={tooltipRef}>
                                    {ref}
                                </span>
                                )
                            </span>
                            <Tooltip
                                anchorSelect={`#${tooltipRef}`}
                                content={t(`spec-footnote${ref}`)}
                                style={{ width: '65%' }}
                            />
                        </React.Fragment>
                    );
                })}
            </>
        ) : (
            <></>
        );
    };

    function compareAttributesIndex(a: any, b: any) {
        if (a[1].index < b[1].index) {
            return -1;
        }
        if (a[1].index > b[1].index) {
            return 1;
        }
        return 0;
    }

    const productGroupFilter = (group: string): any => {
        const productAttributes = product.attributes ? product.attributes : {};

        return Object.entries(productAttributes)
            .filter((attribute: any) => {
                return attribute[1] && attribute[1].group === group;
            })
            .sort(compareAttributesIndex);
    };

    const mainFeatures: any[] = productGroupFilter('General features');

    return (
        <div className='product-specs-listing row'>
            <div
                className={`spec-part spec-part-attributes spec-part1 ${
                    partIndex != 0 && 'hide'
                }`}
            >
                {
                    <>
                        <h4>{t('performance-title')}</h4>

                        {productGroupFilter('Appliance performance').map(
                            (attribute: any, index: number) => {
                                const label = (
                                    <>
                                        {attribute[1].name[currentLocale]}
                                        {createTip(attribute[0])}
                                    </>
                                );
                                return (
                                    <ProductAttributeItem
                                        key={`product-attribute-item${index}`}
                                        label={label}
                                    >
                                        {attribute[1].values[0][currentLocale]}
                                    </ProductAttributeItem>
                                );
                            }
                        )}
                    </>
                }
            </div>
            {[1, 2].map((section_index, index) => (
                <div
                    key={`section_index${index}`}
                    className={classNames(
                        'spec-part spec-part-attributes specs-general',
                        {
                            hide: partIndex != section_index,
                            'show-more': section_index == 2,
                            'hide-mobile': section_index == 2 && !showMore,
                        }
                    )}
                >
                    <h4
                        className={classNames({
                            'show-more': section_index == 2,
                        })}
                    >
                        {t('general-specs-title')}
                    </h4>
                    {(index == 1
                        ? mainFeatures.slice(0, mainFeatures.length / 2)
                        : mainFeatures.slice(mainFeatures.length / 2)
                    )
                        .filter((a) => a[1].name && a[1].values)
                        .map((attribute: any, index) => {
                            return (
                                <ProductAttributeItem
                                    key={`product-attribute-item${index}`}
                                    label={attribute[1].name[currentLocale]}
                                >
                                    {attribute[1].values[0][currentLocale]}
                                </ProductAttributeItem>
                            );
                        })}
                </div>
            ))}

            <div
                className='spec-part see-more'
                onClick={() => setShowMore(!showMore)}
            >
                <span>{showMore ? t('see-less') : t('see-more')}</span>
            </div>
            <div className='spec-prev spec-nav'>
                <span
                    className={
                        partIndex !== 0
                            ? 'iconfont iconfont-prev'
                            : 'iconfont iconfont-prev not-clickable'
                    }
                    onClick={() =>
                        partIndex !== 0 && setPartIndex(partIndex - 1)
                    }
                />
            </div>
            <div className='spec-next spec-nav'>
                <span
                    className={
                        partIndex < 2
                            ? 'iconfont iconfont-next'
                            : 'iconfont iconfont-next not-clickable'
                    }
                    onClick={() => partIndex < 2 && setPartIndex(partIndex + 1)}
                />
            </div>
        </div>
    );
}
